/**
 * Shared food-storage + power-outage triage data.
 *
 * Storage times (fridgeLife/freezerLife): USDA FoodKeeper, FSIS data version 128
 * (the production copy in tests/fixtures/usda-foodkeeper-v128.json, verified equal
 * to the FSIS file on 10/17/2026), under the FRIDGE-837 rule: the number comes from
 * FoodKeeper; when two USDA records apply, the shorter time wins, so no time is
 * longer than FoodKeeper's; "not recommended" is the shortest answer and a package
 * date the longest. A refrigerator time is the shortest of the plain and the
 * once-opened readings (" opened" marks a once-opened time). Freezer times are best
 * quality only: food kept frozen at 0°F stays safe indefinitely. `foodKeeper` names
 * the records beside each time (FRIDGE-1633). Triage-only entries keep them null.
 *
 * Outage-triage fields (discardAfter4hAbove40F, outageVerdict, refreezeGuidance):
 * sourced from the FSIS/HHS "when to save and when to throw it out" charts,
 * both actually loaded 2026-07-10:
 *   [1] https://www.foodsafety.gov/food-safety-charts/food-safety-during-power-outage
 *       (per-food refrigerated + frozen charts; page last reviewed 2024-08-08)
 *   [2] https://www.fsis.usda.gov/food-safety/safe-food-handling-and-preparation/emergencies/keep-your-food-safe-during-emergencies
 *       (the 4h fridge / 48h full / 24h half-full freezer rules + discard lists)
 *
 * Core FSIS rules [2]: a closed refrigerator keeps food safe up to 4 hours;
 * a full freezer ≈48 hours, half-full ≈24 hours; refreeze only if the food
 * still contains ice crystals or is at or below 40°F; never taste to check.
 *
 * The power-outage center renders the triage fields, not the storage times. The
 * Food Storage Guide widget shows the storage times from its own inline copy (a
 * standalone embed script cannot import this module): the spec formula in
 * scripts/data/widget-specs-72.json generates public/widgets/food-storage-calculator.js
 * (scripts/generate-widgets-from-specs.ts), which generates
 * public/widgets/_v2/food-storage-calculator.js (scripts/build-v2-widgets.js).
 * tests/food-storage-data-foodkeeper-fridge-1633.test.ts checks this file and all
 * three copies against FoodKeeper, record by record.
 */

export const OUTAGE_RULES = {
  fridgeSafeHoursClosed: 4,
  freezerFullSafeHours: 48,
  freezerHalfSafeHours: 24,
  dangerTempF: 40,
  // [2] "Discard opened mayonnaise, tartar sauce, and horseradish if they
  // were held above 50°F for over 8 hours."
  mayoDangerTempF: 50,
  mayoDangerHours: 8,
  sources: [
    'https://www.foodsafety.gov/food-safety-charts/food-safety-during-power-outage',
    'https://www.fsis.usda.gov/food-safety/safe-food-handling-and-preparation/emergencies/keep-your-food-safe-during-emergencies',
  ],
  lastVerifiedAt: '2026-07-10',
} as const

export type OutageVerdict = 'discard' | 'keep' | 'check'

/** The USDA FoodKeeper records (usda_id, FSIS data version 128) beside an entry's storage times. */
export interface FoodKeeperBasis {
  /** Every record for the food the entry names; the shortest time among them is the one stated. */
  records: readonly number[]
  /** The record(s) whose refrigerator time fridgeLife states. */
  fridge: readonly number[]
  /** The record(s) whose freezer time freezerLife states. */
  freezer: readonly number[]
}

export interface FoodStorageEntry {
  key: string
  label: string
  /** USDA FoodKeeper refrigerator time (null = no FoodKeeper time for this entry). */
  fridgeLife: string | null
  /** USDA FoodKeeper freezer time, best quality only (null = no FoodKeeper time for this entry). */
  freezerLife: string | null
  /** The FoodKeeper records beside the two times (null for the triage-only entries). */
  foodKeeper: FoodKeeperBasis | null
  /** FSIS: discard if the refrigerator has been without power >4h (i.e. held above 40°F). */
  discardAfter4hAbove40F: boolean
  /** The chart's verdict, one line, faithful to [1]/[2]. */
  outageDetail: string
  /** FSIS refreeze guidance from the frozen-food chart [1]. */
  refreezeGuidance: string
}

export const FOOD_STORAGE_DATA: FoodStorageEntry[] = [
  {
    key: 'raw-chicken',
    label: 'Raw chicken',
    fridgeLife: '1–2 days',
    freezerLife: '9 months',
    // 113 whole chicken, 116–118 chicken parts: the parts' 9 months is shorter than a whole bird's 12.
    foodKeeper: { records: [113, 116, 117, 118], fridge: [113, 116, 117, 118], freezer: [116, 117, 118] },
    discardAfter4hAbove40F: true,
    outageDetail: 'Raw poultry: discard after 4+ hours without power (above 40°F).',
    refreezeGuidance: 'Refreeze only if it still contains ice crystals or is at or below 40°F; discard otherwise.',
  },
  {
    key: 'cooked-chicken',
    label: 'Cooked chicken',
    fridgeLife: '3–4 days',
    freezerLife: '2–3 months',
    // 135 cooked poultry, 136 fried, 141 rotisserie, 173 leftovers with poultry (4–6 months set aside for 2–3).
    foodKeeper: { records: [135, 136, 141, 173], fridge: [135, 136, 141, 173], freezer: [173] },
    discardAfter4hAbove40F: true,
    outageDetail: 'Cooked or leftover meat and poultry: discard after 4+ hours without power.',
    refreezeGuidance: 'Refreeze only if it still contains ice crystals or is at or below 40°F; discard otherwise.',
  },
  {
    key: 'ground-beef',
    label: 'Ground beef (raw)',
    fridgeLife: '1–2 days',
    freezerLife: '3–4 months',
    // 43 ground beef.
    foodKeeper: { records: [43], fridge: [43], freezer: [43] },
    discardAfter4hAbove40F: true,
    outageDetail: 'Raw meat: discard after 4+ hours without power (above 40°F).',
    refreezeGuidance: 'Refreeze only if it still contains ice crystals or is at or below 40°F; discard otherwise.',
  },
  {
    key: 'steak',
    label: 'Steak (raw)',
    fridgeLife: '3–5 days',
    freezerLife: '4–12 months',
    // 40 beef steaks.
    foodKeeper: { records: [40], fridge: [40], freezer: [40] },
    discardAfter4hAbove40F: true,
    outageDetail: 'Raw meat: discard after 4+ hours without power (above 40°F).',
    refreezeGuidance: 'Refreeze only if it still contains ice crystals or is at or below 40°F; discard otherwise.',
  },
  {
    key: 'cooked-rice',
    label: 'Cooked rice',
    fridgeLife: '3–4 days',
    freezerLife: '1–2 months',
    // 178 cooked rice (4–6 days, 6 months) and 174 leftovers without meat, rice named: the shorter 174 wins.
    foodKeeper: { records: [174, 178], fridge: [174], freezer: [174] },
    discardAfter4hAbove40F: true,
    outageDetail: 'Cooked pasta, rice, and potatoes: discard after 4+ hours above 40°F.',
    refreezeGuidance: 'Rice-based casseroles: refreeze only with ice crystals still present; discard if held above 40°F.',
  },
  {
    key: 'eggs',
    label: 'Eggs (in shell)',
    fridgeLife: '3–5 weeks',
    freezerLife: 'Not recommended',
    // 21 eggs in shell.
    foodKeeper: { records: [21], fridge: [21], freezer: [21] },
    discardAfter4hAbove40F: true,
    outageDetail: 'Fresh shell eggs and egg dishes: discard after 4+ hours without power.',
    refreezeGuidance: 'Freezing eggs in the shell is not recommended; out-of-shell egg products refreeze only with ice crystals.',
  },
  {
    key: 'milk',
    label: 'Milk',
    fridgeLife: 'Package use-by date',
    freezerLife: '3 months',
    // 27 milk, plain or flavored: FoodKeeper gives no refrigerator number and defers to the package.
    foodKeeper: { records: [27], fridge: [27], freezer: [27] },
    discardAfter4hAbove40F: true,
    outageDetail: 'Milk, cream, sour cream, yogurt: discard after 4+ hours without power.',
    refreezeGuidance: 'Refreeze with ice crystals still present (some loss of texture); discard if held above 40°F.',
  },
  {
    key: 'cheese-hard',
    label: 'Hard cheese',
    fridgeLife: '3–4 weeks opened',
    freezerLife: '6 months',
    // 3 hard cheese (cheddar, Swiss, block Parmesan): 6 months unopened, 3–4 weeks once opened.
    foodKeeper: { records: [3], fridge: [3], freezer: [3] },
    discardAfter4hAbove40F: false,
    outageDetail: 'Hard cheeses (Cheddar, Colby, Swiss, Parmesan, provolone, Romano): safe to keep.',
    refreezeGuidance: 'Hard cheeses can be refrozen even after thawing above 40°F.',
  },
  {
    key: 'leftovers',
    label: 'Cooked leftovers',
    fridgeLife: '3–4 days',
    freezerLife: '1–2 months',
    // 173 with meat (2–3 months), 174 without meat and 175 pizza (1–2 months): the shorter wins.
    foodKeeper: { records: [173, 174, 175], fridge: [173, 174, 175], freezer: [174, 175] },
    discardAfter4hAbove40F: true,
    outageDetail: 'Casseroles, soups, stews, and leftovers: discard after 4+ hours without power.',
    refreezeGuidance: 'Refreeze only if ice crystals remain or the food is at or below 40°F; discard otherwise.',
  },
  {
    key: 'pizza',
    label: 'Pizza (leftover)',
    fridgeLife: '3–4 days',
    freezerLife: '1–2 months',
    // 175 leftover pizza.
    foodKeeper: { records: [175], fridge: [175], freezer: [175] },
    discardAfter4hAbove40F: true,
    outageDetail: 'Pizza with any topping: discard after 4+ hours without power.',
    refreezeGuidance: 'Frozen pizza: refreeze only with ice crystals still present; discard if held above 40°F.',
  },
  {
    key: 'bread',
    label: 'Bread',
    fridgeLife: '2–3 weeks opened',
    freezerLife: '3 months',
    // 195 commercial bread (2–3 weeks once opened, 3–5 months frozen), 460/461 whole wheat (3 months frozen),
    // 574 homemade (2–3 months in the refrigerator): the shorter time wins in each place.
    foodKeeper: { records: [195, 460, 461, 574], fridge: [195], freezer: [460, 461] },
    discardAfter4hAbove40F: false,
    outageDetail: 'Bread, rolls, cakes, muffins, tortillas: safe to keep (unless contaminated by raw-meat juices).',
    refreezeGuidance: 'Breads and rolls can be refrozen even after thawing.',
  },
  {
    key: 'berries',
    label: 'Fresh berries',
    fridgeLife: '2–3 days',
    freezerLife: '8–12 months',
    // Every fresh berry: 252 cherries and lychee, 253 blackberries, 254 blueberries, 259 cranberries,
    // 481 strawberries, 482 raspberries, 483 cherries; strawberries, raspberries and cherries keep the shortest.
    foodKeeper: { records: [252, 253, 254, 259, 481, 482, 483], fridge: [481, 482, 483], freezer: [254, 481, 482, 483] },
    discardAfter4hAbove40F: false,
    outageDetail: 'Uncut fresh fruit: safe to keep. (Cut fruit: discard after 4+ hours.)',
    refreezeGuidance: 'Frozen fruit: refreeze; discard if mold, a yeasty smell, or sliminess develops.',
  },
  {
    key: 'deli-meat',
    label: 'Deli meat',
    fridgeLife: '3–5 days opened',
    freezerLife: '1–2 months',
    // 184 store-sliced, 192 pre-packaged, 514–517 pre-packaged turkey, ham, salami, chicken.
    foodKeeper: { records: [184, 192, 514, 515, 516, 517], fridge: [184, 192, 514, 515, 516, 517], freezer: [184, 192, 514, 515, 516, 517] },
    discardAfter4hAbove40F: true,
    outageDetail: 'Lunchmeats, hot dogs, bacon, sausage: discard after 4+ hours without power.',
    refreezeGuidance: 'Refreeze only if ice crystals remain or the food is at or below 40°F; discard otherwise.',
  },
  {
    key: 'fish-raw',
    label: 'Raw fish',
    fridgeLife: '1–2 days',
    freezerLife: '2–3 months',
    // 144/145 lean fish (4–8 months frozen), 146 fatty fish (2–3 months): the shorter wins.
    foodKeeper: { records: [144, 145, 146], fridge: [144, 145, 146], freezer: [146] },
    discardAfter4hAbove40F: true,
    outageDetail: 'Raw fish and seafood: discard after 4+ hours without power (above 40°F).',
    refreezeGuidance: 'Refreeze only if it still contains ice crystals or is at or below 40°F; discard otherwise.',
  },
  {
    key: 'soup',
    label: 'Soup / stew',
    fridgeLife: '3–4 days',
    freezerLife: '2–3 months',
    // 193 soups and stews, 629 homemade vegetable soup (3 months frozen, set aside for 2–3).
    foodKeeper: { records: [193, 629], fridge: [193, 629], freezer: [193] },
    discardAfter4hAbove40F: true,
    outageDetail: 'Soups and stews: discard after 4+ hours without power.',
    refreezeGuidance: 'Refreeze only if ice crystals remain; discard if held above 40°F.',
  },
  {
    key: 'butter',
    label: 'Butter',
    fridgeLife: '1–2 months',
    freezerLife: '6–9 months',
    // 1 butter.
    foodKeeper: { records: [1], fridge: [1], freezer: [1] },
    discardAfter4hAbove40F: false,
    outageDetail: 'Butter and margarine: safe to keep.',
    refreezeGuidance: 'Butter and margarine can be refrozen.',
  },
  // ---- Triage-only entries below (outage charts [1]/[2]; storage times not
  // ---- FoodKeeper-verified in this packet, so fridgeLife/freezerLife stay null).
  {
    key: 'cheese-soft',
    label: 'Soft cheese',
    fridgeLife: null,
    freezerLife: null,
    foodKeeper: null,
    discardAfter4hAbove40F: true,
    outageDetail: 'Soft cheeses (Brie, cottage, cream, mozzarella, ricotta…): discard after 4+ hours without power.',
    refreezeGuidance: 'Refreeze with ice crystals still present (some loss of texture); discard if held above 40°F.',
  },
  {
    key: 'yogurt',
    label: 'Yogurt',
    fridgeLife: null,
    freezerLife: null,
    foodKeeper: null,
    discardAfter4hAbove40F: true,
    outageDetail: 'Yogurt: discard after 4+ hours without power.',
    refreezeGuidance: 'Refreeze only if ice crystals remain; discard otherwise.',
  },
  {
    key: 'mayo-opened',
    label: 'Mayonnaise (opened)',
    fridgeLife: null,
    freezerLife: null,
    foodKeeper: null,
    discardAfter4hAbove40F: false,
    outageDetail: 'Opened mayonnaise, tartar sauce, horseradish: discard only if held above 50°F for more than 8 hours.',
    refreezeGuidance: 'Not a freezer item.',
  },
  {
    key: 'condiments',
    label: 'Condiments (ketchup, mustard, pickles)',
    fridgeLife: null,
    freezerLife: null,
    foodKeeper: null,
    discardAfter4hAbove40F: false,
    outageDetail: 'High-acid condiments — mustard, ketchup, relish, pickles, jams: safe to keep (may spoil sooner).',
    refreezeGuidance: 'Not a freezer item.',
  },
  {
    key: 'produce-whole',
    label: 'Whole fruits & vegetables',
    fridgeLife: null,
    freezerLife: null,
    foodKeeper: null,
    discardAfter4hAbove40F: false,
    outageDetail: 'Uncut fresh fruits and vegetables: safe to keep unless moldy or off-smelling.',
    refreezeGuidance: 'Frozen vegetables: refreeze with ice crystals; discard after 6+ hours above 40°F.',
  },
  {
    key: 'produce-cut',
    label: 'Cut fruits & vegetables',
    fridgeLife: null,
    freezerLife: null,
    foodKeeper: null,
    discardAfter4hAbove40F: true,
    outageDetail: 'Cut fresh fruits and vegetables (and packaged pre-washed greens): discard after 4+ hours without power.',
    refreezeGuidance: 'Refreeze only with ice crystals still present.',
  },
  {
    key: 'juice-opened',
    label: 'Juice (opened)',
    fridgeLife: null,
    freezerLife: null,
    foodKeeper: null,
    discardAfter4hAbove40F: true,
    outageDetail: 'Opened vegetable juice: discard after 4+ hours. Opened fruit juice keeps per the chart — when in doubt, throw it out.',
    refreezeGuidance: 'Frozen fruit juices: refreeze; discard if mold, a yeasty smell, or sliminess develops.',
  },
  {
    key: 'ice-cream',
    label: 'Ice cream / frozen yogurt',
    fridgeLife: null,
    freezerLife: null,
    foodKeeper: null,
    discardAfter4hAbove40F: true,
    outageDetail: 'Ice cream and frozen yogurt: discard once thawed — even if ice crystals remain.',
    refreezeGuidance: 'Never refreeze thawed ice cream or frozen yogurt.',
  },
]

export function getFoodEntry(key: string): FoodStorageEntry | null {
  return FOOD_STORAGE_DATA.find((f) => f.key === key) ?? null
}

/**
 * Triage verdict for the power-outage center tool. Every verdict is traceable
 * to the FSIS rules in OUTAGE_RULES + the per-food chart rows above.
 */
export function outageVerdict(entry: FoodStorageEntry, hoursWithoutPower: number): OutageVerdict {
  if (hoursWithoutPower <= OUTAGE_RULES.fridgeSafeHoursClosed) return 'keep'
  if (entry.key === 'mayo-opened') {
    return hoursWithoutPower > OUTAGE_RULES.mayoDangerHours ? 'discard' : 'check'
  }
  return entry.discardAfter4hAbove40F ? 'discard' : 'keep'
}
